import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../App/redux/store';
import { axiosInstance, updateEntity } from '../../../services/axiosInstanceService';
import LocalStorageService from '../../../services/localStorageService';
import SessionStorageService from '../../../services/sessionStorageService';
import { appManagerActions } from '../../../store/appManager/actions';
import { loaderActions } from '../../../store/loader/actions';
import { NotificationStatus } from '../../../utils/enums';
import { companyActions } from '../../company/companySlice';
import { fetchCompanyThunk } from '../../company/services/companyThunks';
import { fetchSubscriptionThunk } from '../../subscription/services/subscriptionThunks';
import { subscriptionActions } from '../../subscription/subscriptionSlice';
import { fetchUserById, updateUser } from '../../users/services/usersRequests';
import {
  fetchUserMarketResearchThunk,
  fetchUsersThunk,
} from '../../users/services/usersThunks';
import { resetAppAction } from '../authSlice';
import { authNotifications } from '../notifications';
import { login } from './authRequest';

/**
 * Authenticates a user and returns authentication details.
 * @param {string} username - login name
 * @param {string} password - plain text password
 * @return {Object} Authentication result
 * @return {number} return.userId - The user's ID
 * @return {number} return.companyId - The company ID associated with the user
 * @return {string} return.token - The authentication token
 * @return {string} return.refreshToken - The refresh token
 */
const loginThunk = createAsyncThunk(
  'auth/loginThunk',
  async (
    params: { username: string; password: string; navigate: any },
    { dispatch },
  ) => {
    try {
      if (!params.username) throw new Error('Username not found');
      if (!params.password) throw new Error('Password not found');

      dispatch(loaderActions.loaderDisplay.visible());

      const result = await login(params);

      // set datas in local storage
      LocalStorageService.setToken(result.token);
      LocalStorageService.setRefreshToken(result.refreshToken);

      dispatch(loaderActions.loaderDisplay.hidden());

      return result;
    } catch (error: any) {
      const datas = error?.data;
      let message = datas.message;
      if (datas?.status === 401) {
        if (datas.message === 'Invalid credentials.') {
          dispatch(
            appManagerActions.actionNotificationDisplay.visible({
              title: 'Connexion',
              content: `Vote compte utilisateur n'est pas activé.`,
              status: NotificationStatus.FAILURE,
            }),
          );
        } else {
          message = 'user account is not enabled';
        }
      }
      dispatch(loaderActions.loaderDisplay.hidden());
      return Promise.reject(message ? { message } : error);
    }
  },
);
/**
 * Async thunk action to log out the user.
 * It resets the application state and clears local and session storage.
 *
 * @async
 * @function logoutThunk
 * @returns {Promise<void>} A promise that resolves when the logout process is complete.
 */
const logoutThunk = createAsyncThunk(
  'auth/logoutThunk',
  async (params: void, { dispatch }) => {
    try {
      dispatch(resetAppAction());

      LocalStorageService.clear();
      SessionStorageService.clear();
    } catch (error) {
      return Promise.reject(error);
    }
  },
);
/**
 * Async thunk to fetch the authenticated user by their IRI.
 *
 * This function retrieves user data from the API based on the provided `userIdIri`,
 * then dispatches related actions to update the store with company, subscription,
 * and user-related information.
 *
 * @param {Object} params - The parameters required to fetch the authenticated user.
 * @param {string} params.userIdIri - The unique IRI identifier of the user.
 * @param {Object} thunkAPI - The thunkAPI object, allowing access to `dispatch`.
 * @param {Function} thunkAPI.dispatch - Redux dispatch function.
 * @returns {Promise<IUser>} The fetched user entity.
 * @throws {Error} Throws an error if the fetch operation fails.
 */
const fetchAuthUserThunk = createAsyncThunk(
  'auth/authUser',
  async (params: { userIdIri: string }, { dispatch }) => {
    try {
      // Fetch user data from the API
      const result: UserParsedResponse = await fetchUserById(params.userIdIri);

      // If the user is a manager or admin, fetch company and subscription details
      if (result.user.isManager || result.user.isAdmin) {
        dispatch(fetchCompanyThunk({ companyIdIri: result.companyIdIri }));
        dispatch(
          fetchSubscriptionThunk({ subscriptionIdIri: result.subscriptionIdIri }),
        );
      } else {
        // Otherwise, directly set company and subscription IRIs in the store
        dispatch(companyActions.setCompanyIdIri(result.companyIdIri));
        dispatch(subscriptionActions.setSubscriptionIdIri(result.subscriptionIdIri));
      }

      // Fetch other users related to the same company
      dispatch(fetchUsersThunk({ companyIdIri: result.companyIdIri }));

      // Fetch market research data asynchronously (non-blocking)
      dispatch(fetchUserMarketResearchThunk({ userIdIri: params.userIdIri }));

      // Return the authenticated user data
      return result.user;
    } catch (error: any) {
      // Reject the promise if an error occurs
      return Promise.reject(error);
    }
  },
);

/**
 * Async thunk action to update an authenticated user by their ID.
 *
 * @async
 * @function updateAuthUserThunk
 * @param {Object} params - The parameters for fetching the user.
 * @param {string} params.userIdIri - The unique identifier (IRI) of the user.
 * @param {UpdateUserParams} params.body - The unique identifier (IRI) of the user.
 * @returns {Promise<IUser>} A promise that resolves with the user data.
 * @throws {Error} If the request fails, the error is rejected.
 */
const updateAuthUserThunk = createAsyncThunk(
  'auth/updateAuthUserThunk',
  async (
    params: { userIdIri: string | null; body: UpdateUserParams },
    { dispatch },
  ) => {
    try {
      dispatch(loaderActions.loaderDisplay.visible());
      const body = {
        first_name: params.body.firstName,
        last_name: params.body.lastName,
        email: params.body.email,
        phone_number: params.body.phoneNumber,
        enable_newsletter: params.body.enableNewsLetter,
      };

      const result: IUser = await updateUser(params.userIdIri, body);
      dispatch(loaderActions.loaderDisplay.hidden());

      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          authNotifications.updateUserDetails('').success(),
        ),
      );
      return result;
    } catch (error: any) {
      dispatch(
        appManagerActions.actionNotificationDisplay.visible(
          authNotifications.updateUserDetails('').failure(),
        ),
      );
      dispatch(loaderActions.loaderDisplay.hidden());
      return Promise.reject(error);
    }
  },
);
/**
 * Async thunk action to change auth user password.
 *
 * @async
 * @function passwordChangeThunk
 * @param {Object} params - The parameters for fetching the user.
 * @param {string} params.oldPwd - Old password to be changed.
 * @param {string} params.newPwd - New password to save.
 * @returns {Promise} A promise that resolves with no content.
 * @throws {Error} If the request fails, the error is rejected.
 */
const passwordChangeThunk = createAsyncThunk(
  'auth/passwordChangeThunk',
  async (params: { oldPwd: string; newPwd: string }, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const { userIdIri } = state.auth;

      if (!params.oldPwd) throw new Error('Old password not found');
      if (!params.newPwd) throw new Error('New password not found');
      if (!userIdIri) throw new Error('User idIri not found');

      dispatch(loaderActions.loaderDisplay.visible());

      const parsedBody = {
        last_password: params.oldPwd,
        new_password: params.newPwd,
      };

      await updateEntity({
        idIri: `${userIdIri}/update-password`,
        body: parsedBody,
      });

      dispatch(loaderActions.loaderDisplay.hidden());
      dispatch(
        appManagerActions.actionNotificationDisplay.visible({
          title: 'Mot de passe',
          content: `Votre changement de mot de passe a été effectuée avec succès`,
          status: NotificationStatus.SUCCESS,
        }),
      );
    } catch (error) {
      dispatch(loaderActions.loaderDisplay.hidden());
      dispatch(
        appManagerActions.actionNotificationDisplay.visible({
          title: 'Mot de passe',
          content: `Votre changement de mot de passe a échoué`,
          status: NotificationStatus.FAILURE,
        }),
      );
      return Promise.reject(error);
    }
  },
);
/**
 * Async thunk action to reset forgotten auth user password.
 *
 * @async
 * @function forgotPasswordThunk
 * @param {Object} params - The parameters for fetching the user.
 * @param {string} params.username - Email of user to be changed.
 * @param {string} params.navigate - react dom navigate for redirect to /connexion.
 * @returns {Promise} A promise that resolves with no content.
 * @throws {Error} If the request fails, the error is rejected.
 */
const askforPasswordResetLinkThunk = createAsyncThunk(
  'auth/askforPasswordResetLinkThunk',
  async (params: { username: string; navigate: any }, { dispatch }) => {
    try {
      if (!params.username) throw new Error('Mail not found');

      await axiosInstance().post('/reset-password-request', {
        username: params.username,
      });

      params.navigate('/connexion');
    } catch (error) {
      return Promise.reject(error);
    }
  },
);
/**
 * Async thunk action to reset forgotten auth user password.
 *
 * @async
 * @function resetPasswordThunk
 * @param {Object} params - The parameters for fetching the user.
 * @param {string} params.username - Email of user.
 * @param {string} params.password - new password.
 * @param {string} params.username - reset password token.
 * @param {boolean} params.isNew - forgotten or first password set.
 * @param {string} params.navigate - react dom navigate for redirect to /connexion.
 * @returns {Promise} A promise that resolves with no content.
 * @throws {Error} If the request fails, the error is rejected.
 */
const resetOrCreatePasswordThunk = createAsyncThunk(
  'auth/resetPasswordThunk',
  async (
    params: {
      username: string | null;
      password: string;
      token: string | null;
      isNew?: boolean;
      navigate: any;
    },
    { dispatch },
  ) => {
    const { username, token, password, isNew, navigate } = params;
    try {
      if (!params.username) throw new Error('Username not found');
      if (!params.password) throw new Error('Password not found');
      if (!params.token) throw new Error('Token not found');

      const url = isNew ? '/set-password' : '/reset-password';
      await axiosInstance().post(url, { username, token, password });

      dispatch(loaderActions.loaderDisplay.hidden());

      navigate('/connexion');
    } catch (error) {
      dispatch(
        appManagerActions.actionNotificationDisplay.visible({
          title: isNew ? 'Premier mot de passe' : 'Changement de mot de passe',
          content: `${
            isNew
              ? 'Votre premier mot de passe'
              : 'Votre demande de changement de mot de passe'
          } n'a pas pu être envoyée`,
          status: NotificationStatus.FAILURE,
        }),
      );
      return Promise.reject(error);
    }
  },
);

export {
  askforPasswordResetLinkThunk,
  fetchAuthUserThunk,
  loginThunk,
  logoutThunk,
  passwordChangeThunk,
  resetOrCreatePasswordThunk,
  updateAuthUserThunk,
};
